import React, { useState, useCallback } from "react";
import Icon from "components/AppIcon";
import PasswordStrengthMeter from "./PasswordStrengthMeter";
import api from "../../../lib/api";

// Simple toast component
function Toast({ toasts }) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts?.map((t) => (
        <div
          key={t?.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-xl text-sm font-caption text-white backdrop-blur-sm border transition-all duration-300 ${
            t?.type === "success" ?"bg-emerald-600/90 border-emerald-500/40"
              : t?.type === "error" ?"bg-red-600/90 border-red-500/40" :"bg-slate-700/90 border-white/10"
          }`}
        >
          {t?.type === "success" && <Icon name="CheckCircle" size={16} />}
          {t?.type === "error" && <Icon name="AlertCircle" size={16} />}
          {t?.type === "info" && <Icon name="Info" size={16} />}
          <span>{t?.message}</span>
        </div>
      ))}
    </div>
  );
}

export default function ResetPasswordForm({ onSuccess, token }) {
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev?.filter((t) => t?.id !== id));
    }, 4000);
  }, []);

  const validate = () => {
    const errs = {};
    if (!formData?.password) errs.password = "New password is required";
    else if (formData?.password?.length < 8) errs.password = "Password must be at least 8 characters";
    if (!formData?.confirmPassword) errs.confirmPassword = "Please confirm your new password";
    else if (formData?.password !== formData?.confirmPassword) errs.confirmPassword = "Passwords do not match";
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (authError) setAuthError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      addToast("Please fix the errors before submitting.", "error");
      return;
    }
    setIsLoading(true);
    setAuthError("");
    addToast("Updating your password...", "info");

    try {
      await api.post('/auth/reset-password', { token, newPassword: formData?.password });
      setIsLoading(false);
      addToast("Password updated successfully!", "success");
      setTimeout(() => onSuccess?.(), 600);
    } catch (err) {
      setIsLoading(false);
      const msg = err.response?.data?.message || "Failed to reset password. Please try again.";
      setAuthError(msg);
      addToast(msg, "error");
    }
  };

  const passwordsMatch = formData?.confirmPassword && formData?.password === formData?.confirmPassword;

  return (
    <>
      <Toast toasts={toasts} />

      {authError && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 mb-4">
          <Icon name="AlertCircle" size={16} color="#f87171" className="shrink-0 mt-0.5" />
          <p className="text-xs text-red-400 font-caption leading-relaxed">{authError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* New Password */}
        <div className="space-y-1.5">
          <label htmlFor="new-password" className="text-sm font-medium text-slate-300 font-caption">
            New Password
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
              <Icon name="Lock" size={16} />
            </div>
            <input
              id="new-password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData?.password}
              onChange={handleChange}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              className={`w-full h-11 pl-10 pr-11 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all duration-200 ${
                errors?.password ? "border-red-500/60 bg-red-500/5" : "border-white/10 focus:border-teal-500/50"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
            </button>
          </div>
          <PasswordStrengthMeter password={formData?.password} />
          {errors?.password && (
            <p className="text-xs text-red-400 font-caption">{errors?.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-1.5">
          <label htmlFor="confirm-password" className="text-sm font-medium text-slate-300 font-caption">
            Confirm New Password
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
              <Icon name="ShieldCheck" size={16} />
            </div>
            <input
              id="confirm-password"
              type={showConfirm ? "text" : "password"}
              name="confirmPassword"
              value={formData?.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              className={`w-full h-11 pl-10 pr-11 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all duration-200 ${
                errors?.confirmPassword ? "border-red-500/60 bg-red-500/5" : "border-white/10 focus:border-teal-500/50"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
            >
              <Icon name={showConfirm ? "EyeOff" : "Eye"} size={16} />
            </button>
          </div>
          {passwordsMatch && (
            <p className="text-xs text-emerald-400 font-caption flex items-center gap-1">
              <Icon name="Check" size={12} /> Passwords match
            </p>
          )}
          {errors?.confirmPassword && (
            <p className="text-xs text-red-400 font-caption">{errors?.confirmPassword}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.98] text-white font-semibold text-sm font-caption transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-teal-900/30 mt-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Updating password...
            </>
          ) : (
            <>
              Reset Password
              <Icon name="ShieldCheck" size={16} />
            </>
          )}
        </button>
      </form>
    </>
  );
}
